'use client';

import { create } from 'zustand';

/** How long a claim stays "fresh" — after this the marks drop even if the
 *  achievements refetch never arrived, so a later render can't replay the pop. */
const JUST_CLAIMED_TTL_MS = 8000;

interface ClaimSheetState {
  isOpen: boolean;
  justClaimed: ReadonlySet<string>;
  open: () => void;
  close: () => void;
  markJustClaimed: (keys: string[]) => void;
  clearJustClaimed: () => void;
}

let clearTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Drives the single app-level achievement claim sheet (mounted via
 * `AchievementClaimSheet`). Opened from the achievements banner and from
 * unclaimed achievement cards.
 *
 * `justClaimed` carries the keys the last claim covered. It answers WHO — the
 * grid replays the settle pop only on those tiles. WHEN is decided by the card
 * itself, on the render where its own reward flips to credited: the claim
 * mutation only invalidates the achievements query, so the tiles turn green a
 * network round trip after the request resolved.
 */
export const useClaimSheetStore = create<ClaimSheetState>((set) => ({
  isOpen: false,
  justClaimed: new Set<string>(),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  markJustClaimed: (keys) => {
    clearTimeout(clearTimer);
    set({ justClaimed: new Set(keys) });
    clearTimer = setTimeout(() => set({ justClaimed: new Set<string>() }), JUST_CLAIMED_TTL_MS);
  },
  clearJustClaimed: () => {
    clearTimeout(clearTimer);
    set({ justClaimed: new Set<string>() });
  },
}));

export const useOpenClaimSheet = () => useClaimSheetStore((s) => s.open);
